import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePocketBase } from '../contexts/PocketBaseContext';
import { useAdmin } from '../contexts/AdminContext';
import BookCard from '../components/BookCard';
import SearchBar from '../components/SearchBar';
import TagFilter from '../components/TagFilter';

const { FiBook, FiTrendingUp, FiUsers, FiStar, FiChevronDown, FiArrowUpDown } = FiIcons;

const HomePage = () => {
  const { apiCall, loading: globalLoading, user, isAdmin } = usePocketBase();
  const { openAdminPanel } = useAdmin();
  const [books, setBooks] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const [booksPerPage, setBooksPerPage] = useState(20); // Default to 20
  const [showPerPageDropdown, setShowPerPageDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('created'); // 'created', 'title', 'author'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  const booksPerPageOptions = [
    { value: 20, label: '20 books' },
    { value: 50, label: '50 books' },
    { value: 100, label: '100 books' },
    { value: 200, label: '200 books' }
  ];

  const sortOptions = [
    { value: 'created', label: 'Recently Added' },
    { value: 'title', label: 'Title' },
    { value: 'author', label: 'Author' }
  ];

  useEffect(() => {
    fetchBooks();
    fetchTags();
  }, [currentPage, searchQuery, selectedTags, booksPerPage, sortBy, sortOrder]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      let filter = 'public=true';
      
      // Enhanced search to include summary/description
      if (searchQuery) {
        filter += ` && (title ~ "${searchQuery}" || author ~ "${searchQuery}" || summary ~ "${searchQuery}")`;
      }
      
      if (selectedTags.length > 0) {
        const tagFilters = selectedTags.map(tagId => `tags ~ "${tagId}"`).join(' || ');
        filter += ` && (${tagFilters})`;
      }

      // Build sort parameter
      let sortParam = '';
      if (sortBy === 'created') {
        sortParam = sortOrder === 'desc' ? '-created' : 'created';
      } else if (sortBy === 'title') {
        sortParam = sortOrder === 'desc' ? '-title' : 'title';
      } else if (sortBy === 'author') {
        sortParam = sortOrder === 'desc' ? '-author' : 'author';
      }

      const response = await apiCall('GET', `/api/collections/books/records?filter=${encodeURIComponent(filter)}&expand=tags&sort=${sortParam}&page=${currentPage}&perPage=${booksPerPage}`);
      setBooks(response.items || []);
      setTotalPages(response.totalPages || 1);
      setTotalBooks(response.totalItems || 0);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error(error.message || 'Failed to fetch books');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await apiCall('GET', '/api/collections/tags/records?sort=name');
      setTags(response.items || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleBooksPerPageChange = (value) => {
    setBooksPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
    setShowPerPageDropdown(false);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc'); // Default to ascending for new field
    }
    setCurrentPage(1); // Reset to first page when changing sort
  };

  const handleEditBook = (book) => {
    if (!isAdmin) {
      toast.error('Only administrators can edit books');
      return;
    }
    // This will be handled by the AdminPanel component
    // We need to pass the book to edit to the AdminPanel
    window.dispatchEvent(new CustomEvent('editBook', { detail: book }));
    openAdminPanel();
  };

  const stats = [
    { label: 'Total Books', value: totalBooks.toLocaleString(), icon: FiBook, color: 'from-blue-500 to-cyan-500' },
    { label: 'Active Readers', value: '5,678', icon: FiUsers, color: 'from-green-500 to-emerald-500' },
    { label: 'Popular Titles', value: '89', icon: FiTrendingUp, color: 'from-purple-500 to-pink-500' },
    { label: 'Avg Rating', value: '4.8', icon: FiStar, color: 'from-orange-500 to-red-500' }
  ];

  return (
    <div className="min-h-screen">
      {/* Loading Indicator */}
      {globalLoading && (
        <div className="fixed top-20 right-4 z-40 bg-white rounded-lg shadow-lg p-3 flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-600"></div>
          <span className="text-sm text-gray-600">Loading...</span>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              My Book Hub
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-purple-100 max-w-3xl mx-auto">
              Discover, download, and listen to thousands of books. Your digital library awaits.
            </p>
            <motion.div 
              className="flex flex-wrap justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className={`bg-gradient-to-r ${stat.color} rounded-2xl p-6 text-center min-w-[150px]`}
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <SafeIcon icon={stat.icon} className="text-3xl mb-2 mx-auto" />
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-sm opacity-90">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={() => {
                  setCurrentPage(1);
                  fetchBooks();
                }}
              />
              <p className="mt-2 text-sm text-gray-600">
                Search by title, author, or description
              </p>
            </div>
            <div>
              <TagFilter 
                tags={tags}
                selectedTags={selectedTags}
                onChange={setSelectedTags}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Header with Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {loading ? 'Loading...' : `Showing ${books.length} of ${totalBooks.toLocaleString()} books`}
              </h2>
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-1">
                  Search results for "{searchQuery}"
                </p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* Sort Controls */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <div className="flex space-x-1">
                  {sortOptions.map((option) => (
                    <motion.button
                      key={option.value}
                      onClick={() => handleSortChange(option.value)}
                      className={`px-3 py-2 text-sm rounded-lg transition-all ${
                        sortBy === option.value
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {option.label}
                      {sortBy === option.value && (
                        <motion.span
                          animate={{ rotate: sortOrder === 'asc' ? 0 : 180 }}
                          className="inline-block ml-1"
                        >
                          ▲
                        </motion.span>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Books Per Page Dropdown */}
              <div className="relative">
                <motion.button
                  onClick={() => setShowPerPageDropdown(!showPerPageDropdown)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-indigo-500 transition-colors shadow-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-sm text-gray-700">
                    {booksPerPageOptions.find(opt => opt.value === booksPerPage)?.label}
                  </span>
                  <motion.div
                    animate={{ rotate: showPerPageDropdown ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SafeIcon icon={FiChevronDown} className="text-gray-400" />
                  </motion.div>
                </motion.button>

                {/* Dropdown Menu */}
                {showPerPageDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                  >
                    {booksPerPageOptions.map((option) => (
                      <motion.button
                        key={option.value}
                        onClick={() => handleBooksPerPageChange(option.value)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          booksPerPage === option.value
                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                            : 'text-gray-700'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {option.label}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: Math.min(8, booksPerPage) }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl h-80 mb-4"></div>
                  <div className="bg-gray-200 rounded h-4 mb-2"></div>
                  <div className="bg-gray-200 rounded h-3 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : books.length > 0 ? (
            <>
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ staggerChildren: 0.1 }}
              >
                {books.map((book, index) => (
                  <motion.div 
                    key={book.id} 
                    className="h-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <BookCard book={book} onEdit={handleEditBook} />
                  </motion.div>
                ))}
              </motion.div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center mt-12 space-x-2">
                  {/* Previous Button */}
                  <motion.button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg transition-all ${
                      currentPage === 1 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                    whileHover={currentPage !== 1 ? { scale: 1.05 } : {}}
                    whileTap={currentPage !== 1 ? { scale: 0.95 } : {}}
                  >
                    Previous
                  </motion.button>

                  {/* Page Numbers */}
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index + 1;
                      } else if (currentPage <= 3) {
                        pageNum = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + index;
                      } else {
                        pageNum = currentPage - 2 + index;
                      }

                      return (
                        <motion.button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 rounded-lg transition-all ${
                            currentPage === pageNum 
                              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Next Button */}
                  <motion.button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-lg transition-all ${
                      currentPage === totalPages 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                    whileHover={currentPage !== totalPages ? { scale: 1.05 } : {}}
                    whileTap={currentPage !== totalPages ? { scale: 0.95 } : {}}
                  >
                    Next
                  </motion.button>
                </div>
              )}

              {/* Page Info */}
              {totalPages > 1 && (
                <div className="text-center mt-4 text-sm text-gray-600">
                  Page {currentPage} of {totalPages} • {totalBooks.toLocaleString()} total books
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <SafeIcon icon={FiBook} className="text-6xl text-gray-400 mb-4 mx-auto" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No books found</h3>
              <p className="text-gray-500">
                {searchQuery || selectedTags.length > 0 
                  ? 'Try adjusting your search or filters' 
                  : 'No books are available at the moment'
                }
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;